import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { supabase } from '../lib/supabase';
import type { Logo } from '../types/content';

function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [headerLogo, setHeaderLogo] = useState<Logo | null>(null);
  const [, setFavicon] = useState<Logo | null>(null);
  const [isLeaderboardPublished, setIsLeaderboardPublished] = useState(true);
  const [isPressPublished, setIsPressPublished] = useState(true);
  const [isSchedulePublished, setIsSchedulePublished] = useState(true);
  const [isHistoryPublished, setIsHistoryPublished] = useState(true);
  const [isGalleryPublished, setIsGalleryPublished] = useState(true);
  const [isRegistrationEnabled, setIsRegistrationEnabled] = useState(false);
  const location = useLocation();
  const isHomePage = location.pathname === '/';
  const isDashboardPage = location.pathname === '/dashboard';

  useEffect(() => {
    fetchLogos();
    fetchSiteTitle();
    checkRegistrationEnabled();
    checkLeaderboardPublishStatus();
    checkPressPublishStatus();
    checkSchedulePublishStatus();
    checkHistoryPublishStatus();
    checkGalleryPublishStatus();
  }, []);

  const fetchSiteTitle = async () => {
    try {
      const { data } = await supabase
        .from('system_settings')
        .select('value')
        .eq('key', 'site_title')
        .maybeSingle();
      if (data?.value) {
        document.title = String(data.value);
      }
    } catch {}
  };

  const checkRegistrationEnabled = async () => {
    try {
      const { data } = await supabase
        .from('system_settings')
        .select('value')
        .eq('key', 'registration_enabled')
        .maybeSingle();
      setIsRegistrationEnabled(data?.value === true || data?.value === 'true');
    } catch {}
  };

  const checkGalleryPublishStatus = async () => {
    try {
      const { data, error } = await supabase
        .from('system_settings')
        .select('value')
        .eq('key', 'gallery_published')
        .maybeSingle();
      
      if (error && !error.message.includes('No rows found')) {
        console.error('Error checking gallery publish status:', error);
        return;
      }
      
      // If the setting exists, use its value, otherwise default to true
      setIsGalleryPublished(data?.value === true || data?.value === 'true');
    } catch (err) {
      console.error('Error checking gallery publish status:', err);
    }
  };

  const checkPressPublishStatus = async () => {
    try {
      const { data, error } = await supabase
        .from('system_settings')
        .select('value')
        .eq('key', 'press_published')
        .maybeSingle();
      
      if (error && !error.message.includes('No rows found')) {
        console.error('Error checking press publish status:', error);
        return;
      }
      
      // If the setting exists, use its value, otherwise default to true
      setIsPressPublished(data?.value === true || data?.value === 'true');
    } catch (err) {
      console.error('Error checking press publish status:', err);
    }
  };

  const checkSchedulePublishStatus = async () => {
    try {
      const { data, error } = await supabase
        .from('system_settings')
        .select('value')
        .eq('key', 'schedule_published')
        .maybeSingle();
      
      if (error && !error.message.includes('No rows found')) {
        console.error('Error checking schedule publish status:', error);
        return;
      }
      
      // If the setting exists, use its value, otherwise default to true
      setIsSchedulePublished(data?.value === true || data?.value === 'true');
    } catch (err) {
      console.error('Error checking schedule publish status:', err);
    }
  };

  const checkHistoryPublishStatus = async () => {
    try {
      const { data, error } = await supabase
        .from('system_settings')
        .select('value')
        .eq('key', 'history_published')
        .maybeSingle();
      
      if (error && !error.message.includes('No rows found')) {
        console.error('Error checking history publish status:', error);
        return;
      }
      
      // If the setting exists, use its value, otherwise default to true
      setIsHistoryPublished(data?.value === true || data?.value === 'true');
    } catch (err) {
      console.error('Error checking history publish status:', err);
    }
  };

  const checkLeaderboardPublishStatus = async () => {
    try {
      const { data, error } = await supabase
        .from('system_settings')
        .select('value')
        .eq('key', 'leaderboard_published')
        .maybeSingle();
      
      if (error && !error.message.includes('No rows found')) {
        console.error('Error checking leaderboard publish status:', error);
        return;
      }
      
      // If the setting exists, use its value, otherwise default to true
      setIsLeaderboardPublished(data?.value === true || data?.value === 'true');
    } catch (err) {
      console.error('Error checking leaderboard publish status:', err);
    }
  };

  const fetchLogos = async () => {
    try {
      try {
        const { data: headerData, error: headerError } = await supabase
          .from('logos')
          .select('*')
          .eq('type', 'primary')
          .eq('location', 'header')
          .eq('is_active', true)
          .limit(1)
          .maybeSingle();

        if (headerError) {
          console.error('Error fetching header logo:', headerError);
        } else {
          setHeaderLogo(headerData);
        }
      } catch (logoError) {
        console.error('Error in header logo fetch:', logoError);
      }

      try {
        const { data: faviconData, error: faviconError } = await supabase
          .from('logos')
          .select('*')
          .eq('type', 'favicon')
          .eq('is_active', true)
          .limit(1)
          .maybeSingle();

        if (faviconError) {
          console.error('Error fetching favicon:', faviconError);
          return;
        }

        setFavicon(faviconData);
        
        if (faviconData?.url) {
          // Skip the fetch test as it's causing network errors
          const faviconElement = document.querySelector('link[rel="icon"]') as HTMLLinkElement;
          if (faviconElement) {
            faviconElement.href = faviconData.url;
          } else {
            const newFavicon = document.createElement('link');
            newFavicon.rel = 'icon';
            newFavicon.href = faviconData.url;
            document.head.appendChild(newFavicon);
          }
        }
      } catch (faviconError) {
        console.error('Error in favicon fetch:', faviconError);
      }
    } catch (err) {
      console.error('Error fetching logos:', err);
    }
  };

  // Handle scroll events
  if (typeof window !== 'undefined') {
    window.addEventListener('scroll', () => {
      setIsScrolled(window.scrollY > 50);
    });
  }

  const getNavStyles = () => {
    const baseStyles = {
      width: '100%',
      zIndex: 50
    };

    if (isDashboardPage) {
      return {
        ...baseStyles,
        position: 'absolute' as const,
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        backdropFilter: 'blur(8px)',
        borderBottom: '1px solid rgba(229, 231, 235, 0.5)'
      };
    }

    if (isHomePage) {
      return {
        ...baseStyles,
        position: 'fixed' as const,
        backgroundColor: isScrolled ? 'rgba(255, 255, 255, 0.9)' : 'transparent',
        backdropFilter: isScrolled ? 'blur(8px)' : 'none',
        borderBottom: isScrolled ? '1px solid rgba(229, 231, 235, 0.5)' : 'none'
      };
    }

    return {
      ...baseStyles,
      position: 'fixed' as const,
      backgroundColor: 'rgba(255, 255, 255, 0.9)',
      backdropFilter: 'blur(8px)',
      borderBottom: '1px solid rgba(229, 231, 235, 0.5)'
    };
  };

  const getLinkStyles = (isScrolled: boolean) => {
    if (isHomePage) {
      return isScrolled ? 'text-gray-700 hover:text-golf-orange' : 'text-white hover:text-golf-orange';
    }
    return 'text-gray-700 hover:text-golf-orange';
  };

  const getLogoStyles = (isScrolled: boolean) => {
    if (isHomePage) {
      return isScrolled ? 'text-golf-orange' : 'text-white';
    }
    return 'text-golf-orange';
  };

  return (
    <motion.nav
      initial={false}
      style={getNavStyles()}
      className="w-full z-50 transition-all duration-300"
    >
      <div className="container mx-auto px-4">
        <div className="flex justify-between items-center h-20">
          <Link to="/" className="flex items-center">
            {headerLogo ? (
              <img
                src={headerLogo.url}
                alt="Ibom Open"
                className={`h-12 ${headerLogo.format === 'svg' ? 'w-auto' : ''}`}
                width={headerLogo.width}
                height={headerLogo.height}
              />
            ) : (
              <span className={`text-2xl font-display font-bold transition-colors duration-300 ${getLogoStyles(isScrolled)}`}>
                Ibom Open
              </span>
            )}
          </Link>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center space-x-8">
            <Link
              to="/"
              className={`transition-colors duration-300 ${getLinkStyles(isScrolled)}`}
            >
              Home
            </Link>
            <Link 
              to="/about" 
              className={`transition-colors duration-300 ${getLinkStyles(isScrolled)}`}
            >
              About
            </Link>
            {isSchedulePublished && (
              <Link 
                to="/schedule" 
                className={`transition-colors duration-300 ${getLinkStyles(isScrolled)}`}
              >
                Schedule
              </Link>
            )}
            {isHistoryPublished && (
              <Link 
                to="/history" 
                className={`transition-colors duration-300 ${getLinkStyles(isScrolled)}`}
              >
                History
              </Link>
            )}
            {isGalleryPublished && (
              <Link 
                to="/gallery" 
                className={`transition-colors duration-300 ${getLinkStyles(isScrolled)}`}
              >
                Gallery
              </Link>
            )}
            {isLeaderboardPublished && (
              <Link 
                to="/leaderboard" 
                className={`transition-colors duration-300 ${getLinkStyles(isScrolled)}`}
              >
                Leaderboard
              </Link>
            )}
            <Link 
              to="/sponsors" 
              className={`transition-colors duration-300 ${getLinkStyles(isScrolled)}`}
            >
              Partners
            </Link>
            {isPressPublished && (
              <Link 
                to="/press" 
                className={`transition-colors duration-300 ${getLinkStyles(isScrolled)}`}
              >
                Press
              </Link>
            )}
            {isRegistrationEnabled && (
              <Link
                to="/registration"
                className="modern-button px-6 py-2 text-white rounded-full hover:bg-opacity-90 transition-all"
              >
                Register Now
              </Link>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden"
            onClick={() => setIsOpen(!isOpen)}
          >
            <svg
              className={`w-6 h-6 transition-colors duration-300 ${
                isHomePage && !isScrolled ? 'text-white' : 'text-gray-700'
              }`}
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              {isOpen ? (
                <path d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>

        {/* Mobile Menu */}
        {isOpen && (
          <div className="md:hidden bg-white">
            <div className="px-2 pt-2 pb-3 space-y-1">
              <Link
                to="/"
                className="block px-3 py-2 text-gray-700 hover:text-golf-orange transition-colors"
                onClick={() => setIsOpen(false)}
              >
                Home
              </Link>
              <Link
                to="/about"
                className="block px-3 py-2 text-gray-700 hover:text-golf-orange transition-colors"
                onClick={() => setIsOpen(false)}
              >
                About
              </Link>
              {isSchedulePublished && (
                <Link
                  to="/schedule"
                  className="block px-3 py-2 text-gray-700 hover:text-golf-orange transition-colors"
                  onClick={() => setIsOpen(false)}
                >
                  Schedule
                </Link>
              )}
              {isHistoryPublished && (
                <Link
                  to="/history"
                  className="block px-3 py-2 text-gray-700 hover:text-golf-orange transition-colors"
                  onClick={() => setIsOpen(false)}
                >
                  History
                </Link>
              )}
              {isGalleryPublished && (
                <Link
                  to="/gallery"
                  className="block px-3 py-2 text-gray-700 hover:text-golf-orange transition-colors"
                  onClick={() => setIsOpen(false)}
                >
                  Gallery
                </Link>
              )}
              {isLeaderboardPublished && (
                <Link
                  to="/leaderboard"
                  className="block px-3 py-2 text-gray-700 hover:text-golf-orange transition-colors"
                  onClick={() => setIsOpen(false)}
                >
                  Leaderboard
                </Link>
              )}
              <Link
                to="/sponsors"
                className="block px-3 py-2 text-gray-700 hover:text-golf-orange transition-colors"
                onClick={() => setIsOpen(false)}
              >
                Partners
              </Link>
              {isPressPublished && (
                <Link
                  to="/press"
                  className="block px-3 py-2 text-gray-700 hover:text-golf-orange transition-colors"
                  onClick={() => setIsOpen(false)}
                >
                  Press
                </Link>
              )}
              {isRegistrationEnabled && (
                <Link
                  to="/registration"
                  className="block w-full text-center bg-golf-orange text-white px-6 py-2 rounded-full hover:bg-opacity-90 transition-all"
                  onClick={() => setIsOpen(false)}
                >
                  Register Now
                </Link>
              )}
            </div>
          </div>
        )}
      </div>
    </motion.nav>
  );
}

export default Navbar;